import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import IconButton from 'components/@extended/IconButton';
import AdminGecisModal from 'components/AdminGecisModal';
import axios from 'utils/axios';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, isTopLevel = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // item.target === true → SPA route değil, Django Admin sayfasına giden dış
  // link. Tıklanır tıklanmaz boş bir sekme açılır (popup engelleyiciye takılmaması
  // için senkron window.open) ve arka planda auth/admin-gecis/ ile JWT'den Django
  // session'ı kurulunca sekme hedef URL'e yönlendirilir — ekstra bir onay adımı yok.
  const isExternal = Boolean(item.target);
  const [adminHataAcik, setAdminHataAcik] = useState(false);

  const itemHandler = () => {
    if (isExternal) {
      const yeniSekme = window.open('', '_blank');
      axios
        .post('auth/admin-gecis/')
        .then(() => {
          if (yeniSekme) yeniSekme.location.href = item.url;
        })
        .catch(() => {
          yeniSekme?.close();
          setAdminHataAcik(true);
        });
      return;
    }

    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        ...(isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const matchUrl = item?.link ? item.link : item.url;
  // '/' rotası end:false ile her path'i eşler (kök segment her zaman eşleşir);
  // bu yalnızca Ana Sayfa'da tam eşleşme (end:true) gerektirir, diğer linkler
  // alt sayfalarında da vurgulu kalabilsin diye prefix eşleşmeyi (end:false) korur.
  const isSelected = !!matchPath({ path: matchUrl, end: matchUrl === '/' }, pathname);

  const iconSelectedColor = 'primary.main';

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ListItemButton
          {...(!isExternal && { component: Link, to: item.url, target: '_self' })}
          disabled={item.disabled}
          selected={isSelected}
          sx={(theme) => ({
            zIndex: 1201,
            pl: drawerOpen ? (isTopLevel ? 3 : `${level * 28}px`) : 1.5,
            py: !drawerOpen && level === 1 ? 1.25 : 1,
            ...(drawerOpen && {
              '&:hover': { bgcolor: 'primary.lighter' },
              '&.Mui-selected': {
                bgcolor: 'primary.lighter',
                borderRight: '2px solid',
                borderColor: 'primary.main',
                color: iconSelectedColor,
                '&:hover': { color: iconSelectedColor, bgcolor: 'primary.lighter' }
              }
            }),
            ...(!drawerOpen && {
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
            })
          })}
          onClick={() => itemHandler()}
        >
          {itemIcon && (
            <ListItemIcon
              sx={(theme) => ({
                minWidth: 28,
                color: isSelected ? iconSelectedColor : theme.palette.grey[8],
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: 'secondary.lighter' }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'primary.lighter',
                    '&:hover': { bgcolor: 'primary.lighter' }
                  })
              })}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Typography
                  variant={isTopLevel ? 'subtitle2' : 'h6'}
                  sx={(theme) => ({
                    color: isSelected
                      ? iconSelectedColor
                      : isTopLevel
                        ? 'text.secondary'
                        : theme.palette.grey[8],
                    fontWeight: isTopLevel ? 700 : 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    ...(isTopLevel && { textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' })
                  })}
                >
                  {item.title}
                </Typography>
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
        {(drawerOpen || (!drawerOpen && level !== 1)) &&
          item?.actions &&
          item?.actions.map((action, index) => {
            const ActionIcon = action.icon;
            const callAction = action?.function;
            return (
              <IconButton
                key={index}
                {...(action.type === 'function' && {
                  onClick: (event) => {
                    event.stopPropagation();
                    callAction();
                  }
                })}
                {...(action.type === 'link' && {
                  component: Link,
                  to: action.url,
                  target: action.target ? '_blank' : '_self'
                })}
                color="secondary"
                variant="outlined"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 20,
                  zIndex: 1202,
                  width: 20,
                  height: 20,
                  mr: -1,
                  ml: 1,
                  color: 'secondary.dark',
                  borderColor: isSelected ? 'primary.light' : 'secondary.light',
                  '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
                }}
              >
                <ActionIcon style={{ fontSize: '0.625rem' }} />
              </IconButton>
            );
          })}
      </Box>
      {isExternal && <AdminGecisModal open={adminHataAcik} onClose={() => setAdminHataAcik(false)} />}
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  isTopLevel: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
